import React, { useEffect, useMemo, useRef, useState } from "react";
import { Field, reduxForm } from "redux-form";
import { CircularProgress } from "@mui/material";
import draftToHtml from "draftjs-to-html";
import htmlToDraft from "html-to-draftjs";
import { ContentState, EditorState, convertToRaw } from "draft-js";

import emailEditor from "../../../../../../common/EmailEditor";
import commonStyles from "../../../../../SwmsMasters/SwmsMastersStyle.module.css";
import styles from "../pre-start/JobSwmsFormV3PreStart.module.css";
import JobSwmsV3RescueReadinessQuestionsList from "./JobSwmsV3RescueReadinessQuestionsList";

const getVisibleOrders = (questions = []) => {
  let activeOrder = 0;

  return questions.map((question = {}) => {
    if (question.active) {
      activeOrder += 1;
      return activeOrder;
    }

    return "-";
  });
};

const getHeadingText = (headingContents) => {
  if (typeof headingContents === "string" || typeof headingContents === "number") {
    return `${headingContents}`;
  }

  if (!headingContents || typeof headingContents !== "object") {
    return "";
  }

  const candidates = ["content", "html", "heading", "text", "value"];
  const candidate = candidates.find((key) => (
    typeof headingContents[key] === "string" || typeof headingContents[key] === "number"
  ));

  return candidate ? `${headingContents[candidate]}` : "";
};

const getEditorStateFromHtml = (html) => {
  if (!html) return EditorState.createEmpty();
  const parsedHtml = htmlToDraft(`${html}`);
  return parsedHtml && parsedHtml.contentBlocks
    ? EditorState.createWithContent(ContentState.createFromBlockArray(parsedHtml.contentBlocks))
    : EditorState.createEmpty();
};

const getHtmlFromEditorState = (state) => {
  if (!state) return "";
  const plainText = state.getCurrentContent().getPlainText("\n").replace(/\u00a0/g, " ").trim();
  return plainText ? draftToHtml(convertToRaw(state.getCurrentContent())) : "";
};

export { getHeadingText };
export function JobSwmsV3RescueReadinessContainer({
  serverState = {},
  onRetry = () => {},
  included = true,
  toggle = true,
  onOpenAddQuestion,
  onAddQuestion,
  onOpenEditQuestion,
  onEditQuestion,
  moveQuestionRow = () => {},
  onAfterDropQuestionRow = () => {},
  onCancelQuestions,
  onCancel = () => {},
  onSaveQuestions,
  onSave = () => {},
  hasUnsavedQuestionChanges = false,
  questionInteractionDisabled = false,
  requestInFlight = false,
  initialValues = {},
  change,
  onHeadingContentChange = () => {},
  onStaffSignoffHeadingContentChange = () => {},
}) {
  const questions = useMemo(() => {
    if (Array.isArray(serverState.questions)) {
      return serverState.questions;
    }

    return Array.isArray(serverState.data) ? serverState.data : [];
  }, [serverState.questions, serverState.data]);
  const visibleOrders = useMemo(() => getVisibleOrders(questions), [questions]);
  const addQuestion = onOpenAddQuestion || onAddQuestion || (() => {});
  const editQuestion = onOpenEditQuestion || onEditQuestion || (() => {});
  const initialHeading = initialValues.readiness_checklist_heading || "";
  const initialStaffSignoffHeading = initialValues.readiness_staff_signoff || "";
  const [editorState, setEditorState] = useState(() => getEditorStateFromHtml(initialHeading));
  const [staffSignoffEditorState, setStaffSignoffEditorState] = useState(() => getEditorStateFromHtml(initialStaffSignoffHeading));
  const headingRef = useRef(initialHeading);
  const signoffRef = useRef(initialStaffSignoffHeading);

  useEffect(() => {
    if (initialHeading !== headingRef.current) {
      setEditorState(getEditorStateFromHtml(initialHeading));
      headingRef.current = initialHeading;
    }
  }, [initialHeading]);

  useEffect(() => {
    if (initialStaffSignoffHeading !== signoffRef.current) {
      setStaffSignoffEditorState(getEditorStateFromHtml(initialStaffSignoffHeading));
      signoffRef.current = initialStaffSignoffHeading;
    }
  }, [initialStaffSignoffHeading]);

  const updateHeading = (nextState, fieldName, callback, ref, setter) => {
    setter(nextState);
    const html = getHtmlFromEditorState(nextState);
    ref.current = html;
    change(fieldName, html);
    callback(html);
  };

  return (
    <div className={styles.PreStartContainer}>
      <div className={styles.PreStartHeaderRow}>
        <div>
          <div className={styles.PreStartTitle}>Emergency Readiness</div>
          <div className={styles.PreStartSubtitle}>Manage the emergency readiness questions for this SWMS.</div>
        </div>
        <div className={styles.PreStartActionButtons}>
          <button className={commonStyles.PrimaryButton} type="button" onClick={addQuestion} disabled={questionInteractionDisabled}>
            Add Question
          </button>
        </div>
      </div>

      {serverState.loading ? (
        <div className={styles.PreStartLoadingState}><CircularProgress size={18} /></div>
      ) : null}
      <div className={styles.PreStartHeadingWrap}>
        <div className={commonStyles.Label}>Rescue Readiness Questions Heading</div>
        <div className="swmsv2-custom-draft-editor">
          <Field
            name="readiness_checklist_heading"
            component={emailEditor}
            disabled={questionInteractionDisabled}
            editorState={editorState}
            onEditorStateChange={(nextState) => updateHeading(nextState, "readiness_checklist_heading", onHeadingContentChange, headingRef, setEditorState)}
            otherProps={{ editorStyle: { padding: "0px 4px", minHeight: "110px" } }}
          />
        </div>
      </div>
      {!serverState.loading && questions.length === 0 ? (
        <div className={styles.PreStartEmptyState}>No emergency readiness questions yet.</div>
      ) : null}
      {!serverState.loading && questions.length > 0 ? (
        <div className={styles.PreStartQuestionsWrap}>
          <JobSwmsV3RescueReadinessQuestionsList
            questions={questions}
            visibleOrders={visibleOrders}
            reorderDisabled={questionInteractionDisabled}
            moveQuestionRow={moveQuestionRow}
            onAfterDropQuestionRow={onAfterDropQuestionRow}
            onOpenEditQuestion={editQuestion}
          />
        </div>
      ) : null}
      <div className={styles.PreStartHeadingWrap}>
        <div className={commonStyles.Label}>Rescue Readiness Sign Off Heading</div>
        <div className="swmsv2-custom-draft-editor">
          <Field
            name="readiness_staff_signoff"
            component={emailEditor}
            disabled={questionInteractionDisabled}
            editorState={staffSignoffEditorState}
            onEditorStateChange={(nextState) => updateHeading(nextState, "readiness_staff_signoff", onStaffSignoffHeadingContentChange, signoffRef, setStaffSignoffEditorState)}
            otherProps={{ editorStyle: { padding: "0px 4px", minHeight: "110px" } }}
          />
        </div>
      </div>
    </div>
  );
}

export default reduxForm({
  form: "JobSwmsV3RescueReadinessContainerForm",
  destroyOnUnmount: true,
  enableReinitialize: true
})(JobSwmsV3RescueReadinessContainer);
